<template>
  <b-collapse id="filter-box">
    <v-card flat class="pa-3">
      <v-container fluid>
        <v-row>
          <v-col cols="12" lg="6" class="mb-5">
            <v-text-field
              label="Search by Email, Name, Id, Phone"
              outlined
              dense
              hide-details
              v-model.trim="filters.search"
            ></v-text-field>
          </v-col>
          <v-col cols="12" lg="6" class="mb-5">
            <v-select
              :items="roles"
              label="Role"
              item-text="name"
              item-value="id"
              outlined
              dense
              hide-details
              v-model="filters.role"
            ></v-select>
          </v-col>
          <v-col cols="12" lg="6" class="mb-5">
            <v-select
              :items="callStatusOptions"
              label="Call Status"
              item-text="text"
              item-value="value"
              outlined
              dense
              hide-details
              v-model="filters.call_status"
            ></v-select>
          </v-col>
          <v-col cols="12" lg="6" class="mb-5">
            <v-select
              :items="callTypeOptions"
              label="Call type"
              item-text="text"
              item-value="value"
              outlined
              dense
              hide-details
              v-model="filters.call_type"
            ></v-select>
          </v-col>
          <v-col cols="12" lg="6" class="mb-5">
            <v-select
              :items="outputStatusOptions"
              label="Output Status"
              item-text="text"
              item-value="value"
              outlined
              dense
              hide-details
              v-model="filters.output_status"
            ></v-select>
          </v-col>
          <v-col cols="12" lg="6" class="mb-5">
            <v-select
              :items="botOptions"
              label="Prompt"
              item-text="text"
              item-value="value"
              outlined
              dense
              hide-details
              v-model="filters.bot"
            ></v-select>
          </v-col>
          <v-col cols="12" lg="6" class="mb-5">
            <v-select
              v-model="filters.final_status"
              :items="statusOptions"
              label="Final Status"
              outlined
              dense
              hide-details
              multiple
              chips
              small-chips
              deletable-chips
              clearable
            ></v-select>
          </v-col>
          <v-col cols="12" lg="6" class="mb-5">
            <v-select
              :items="languageOptions"
              label="Language"
              item-text="text"
              item-value="value"
              outlined
              dense
              hide-details
              v-model="filters.language"
            ></v-select>
          </v-col>
          <div class="col-12">
            <v-col cols="12" lg="6" class="mb-5 p-0 pe-3">
              <v-autocomplete
                v-model="filters.client_project_id"
                :items="clientsList"
                :loading="isLoading"
                :search-input.sync="searchQuery"
                @update:search-input="debouncedFetchItems(searchQuery)"
                :no-filter="true"
                small-chips
                clearable
                hide-details
                hide-selected
                return-object
                outlined
                label="Search with Squad Name, Client Email, or Client Name"
                class="ai-autocomplete"
              >
                <template v-slot:no-data>
                  <v-list-item>
                    <v-list-item-title>
                      Search to select a
                      <strong>Client</strong>
                    </v-list-item-title>
                  </v-list-item>
                </template>
                <template v-slot:selection="{ attr, on, item, selected }">
                  <v-chip
                    v-bind="attr"
                    :input-value="selected"
                    color="grey lighten-2"
                    v-on="on"
                  >
                    <span
                      v-if="item.project_internal_name"
                      v-text="
                        `${item.client_name} - ${item.project_internal_name}`
                      "
                    ></span>
                    <span v-else v-text="`${item.client_name}`"></span>
                  </v-chip>
                </template>
                <template v-slot:item="{ item }">
                  <v-list-item-content>
                    <v-list-item-title
                      v-text="`${item.client_name} - ${item.client_email}`"
                    ></v-list-item-title>
                    <v-list-item-subtitle
                      v-text="item.project_internal_name"
                    ></v-list-item-subtitle>
                  </v-list-item-content>
                </template>
              </v-autocomplete>
            </v-col>
          </div>

          <div class="col-6 col-lg-6 mb-5">
            <button
              class="btn btn-info font-weight-bolder font-size-h6 px-5 col"
              @click="applyFilter"
            >
              Apply
            </button>
          </div>
          <div class="col-6 col-lg-6 mb-5">
            <button
              class="btn btn-secondary font-weight-bolder font-size-h6 px-5 col"
              @click="resetQuery"
            >
              Reset
            </button>
          </div>
        </v-row>
      </v-container>
    </v-card>
  </b-collapse>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import debounce from "lodash.debounce";

const initQuery = {
  search: "",
  role: null,
  call_status: null,
  output_status: null,
  call_type: null,
  bot: null,
  final_status: [],
  language: null,
  client_project_id: null,
};

export default {
  name: "AICallResultsFilter",
  data() {
    return {
      filters: { ...initQuery },
      roleOptions: [
        { value: null, text: "All Roles" },
        { value: "Developer", text: "Developer" },
        { value: "Designer", text: "Designer" },
        // Add more roles as needed
      ],
      callStatusOptions: [
        { value: "scheduled", text: "Scheduled" },
        { value: "initiated", text: "Initiated" },
        { value: "completed", text: "Completed" },
        { value: "ai_call_issue", text: "Issue with the Provider" },
      ],
      outputStatusOptions: [
        { value: "ACHIEVED", text: "Message Delivered" },
        { value: "NO_ANSWER", text: "Unreachable" },
        { value: "NOT_ACHIEVED", text: "Message not Delivered" },
      ],
      callTypeOptions: [
        { value: "main", text: "Main" },
        { value: "follow_up", text: "Follow up" },
      ],
      botOptions: [
        { value: "lama", text: "Update Salary and Availability (Lama)" },
        { value: "ghada", text: "Select Slots (Ghada)" },
      ],
      statusOptions: [
        { value: "talent_pending_update", text: "Talent Pending Update" },
        { value: "client_pending_update", text: "Client Pending Update" },
        { value: "talent_pending", text: "Talent Pending" },
        { value: "client_pending", text: "Client Pending" },
        { value: "talent_unavailable", text: "Talent Unavailable" },
        { value: "scheduled", text: "Scheduled" },
        { value: "canceled", text: "Canceled" },
        { value: "talent_no_show", text: "Talent No Show" },
        { value: "client_no_show", text: "Client No Show" },
        { value: "completed", text: "Completed" },
        { value: "accepted", text: "Accepted" },
        { value: "rejected", text: "Rejected" },
        { value: "expired", text: "Expired" },
        { value: "hired", text: "Hired" },
        { value: "unknown_no_show", text: "Unknown No Show" },
        { value: "bot_issue", text: "Bot Issue" },
      ],
      languageOptions: [
        { value: "en", text: "English" },
        { value: "ar", text: "Arabic" },
      ],
      searchQuery: null,
      isLoading: false,
    };
  },
  computed: {
    ...mapGetters({
      roles: "talents/getRoles",
      clientsList: "talentProfile/getClientsList",
    }),
  },
  methods: {
    ...mapActions({
      getRoles: "talents/getRoles",
      getClientsList: "talentProfile/getClientsList",
    }),
    handleGettingClientsList(payload) {
      this.isLoading = true;
      this.getClientsList(payload)
        .then(() => {
          this.isLoading = false;
        })
        .catch((err) => {
          console.log(err);
        })
        .finally(() => (this.isLoading = false));
    },
    applyFilter() {
      const {
        search,
        role,
        call_status,
        output_status,
        call_type,
        bot,
        final_status,
        language,
        client_project_id,
      } = this.filters;

      // Check if at least one filter is applied
      if (
        !search &&
        !role &&
        !call_status &&
        !output_status &&
        !call_type &&
        !bot &&
        !final_status &&
        !language &&
        !client_project_id
      ) {
        this.$toasted.show("Please Add At Least One Filter", {
          type: "error",
          icon: "error",
        });
        return;
      }

      // Prepare data object with applied filters
      let data = {};
      if (search) data.search = search;
      if (role) data.role = role;
      if (call_status) data.call_status = call_status;
      if (output_status) data.output_status = output_status;
      if (call_type) data.call_type = call_type;
      if (bot) data.bot = bot;
      if (final_status) data.source_status = final_status;
      if (language) data.language = language;
      if (client_project_id)
        data.client_project_id = client_project_id?.project_id;
      // Emit the applyFilter event with the data
      this.$emit("applyFilter", data);
    },
    resetQuery() {
      this.filters = { ...initQuery };
      this.$emit("resetQuery");
    },
  },
  created() {
    this.getRoles();
    this.debouncedFetchItems = debounce(this.handleGettingClientsList, 300);
    this.debouncedFetchItems("");
  },
};
</script>

<style scoped>
.v-card {
  margin-bottom: 20px;
  background-color: #ffffff;
  border-radius: 5px;
}
.v-card-title {
  font-weight: bold;
}
</style>
<style>
/* Match v-autocomplete height to exactly 40px (dense) */
.ai-autocomplete .v-input__slot,
.ai-autocomplete .v-input__control,
.ai-autocomplete input {
  min-height: 40px !important;
  height: 40px !important;
  max-height: 40px !important;
  line-height: 40px !important;
  box-sizing: border-box;
  padding-top: 0 !important;
  padding-bottom: 0 !important;
}

/* Adjust chips inside v-autocomplete for consistent height and remove extra margin */
.ai-autocomplete .v-chip {
  height: 24px !important;
  min-height: 24px !important;
  max-height: 24px !important;
  font-size: 0.875rem;
  margin-top: 0 !important;
  margin-bottom: 0 !important;
}

/* Remove extra padding that may cause height issues */
.ai-autocomplete .v-input__slot {
  padding-top: 0 !important;
  padding-bottom: 0 !important;
}

.v-text-field--outlined .v-label {
  top: 12px !important;
}

.v-text-field--full-width .v-input__prepend-outer,
.v-text-field--full-width .v-input__prepend-inner,
.v-text-field--full-width .v-input__append-inner,
.v-text-field--full-width .v-input__append-outer,
.v-text-field--enclosed .v-input__prepend-outer,
.v-text-field--enclosed .v-input__prepend-inner,
.v-text-field--enclosed .v-input__append-inner,
.v-text-field--enclosed .v-input__append-outer {
  margin-top: 10px;
}
</style>
